import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { objectToFormData } from '@/lib/utils';
import hashFile from '@/lib/xxhash';
import {
  useCreateDraftPostMutation,
  useUpdatePostDataMutation,
  useUpdatePostAttachmentMutation,
} from '@/redux/post/post.api.slice';
import { useGetPreSignedUploadUrlMutation } from '@/redux/attachment/attachment.api.slice';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import {
  useCreateTagMutation,
  useGetTagsQuery,
} from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';
import {
  AttachmentParentType,
  IGetPreSignedUploadUrlRequest,
} from '@/redux/attachment/attachment.d';
import { IPostDataUploadRequest } from '@/redux/post/post.d';
import {
  useCreateProjectGroupMutation,
  useUpdateProjectGroupProjectsMutation,
} from '@/redux/projectGroup/projectGroup.api.slice';
import {
  selectV2,
  setPostId,
  setFileAttachmentIds,
  setUploadingFileIds,
  setUploadTotal,
  incrementUploadProgress,
  setGroupId,
  addChildId,
} from './v2Upload.slice';
import { pickThumbnailFile } from './utils/fileHelpers';
import {
  attachmentCategoryFor,
  attachmentResourceTypeFor,
  buildBasicsMetaPatch,
  buildJobDetailsPatch,
  buildModelGroups,
  buildParserPatch,
  extractErrorMessage,
  fileAttachmentIdMap,
  fileForUpload,
  uploadFileName,
  uploadFileToPresignedUrl,
} from './utils/postSync';
import { V2File } from './v2Upload.d';

export function uploadRequestFor(
  f: V2File,
  parentId: string,
  fileHash: string
): IGetPreSignedUploadUrlRequest['attachments'][number] {
  // unmoved only: a moved file's name carries the user's pick instead
  const parsed = f.kind === 'gcode' ? f.extracted?.controllerType : null;
  return {
    parentId,
    parentType: AttachmentParentType.POST,
    category: attachmentCategoryFor(f.kind),
    fileName: uploadFileName(f),
    fileHash,
    fileSize: f.sizeBytes,
    resourceType: attachmentResourceTypeFor(f),
    ...(parsed && parsed === f.controller && { controller: parsed }),
  };
}

/** `updateAttachments` zips every attachment it is handed when currentPage is
 * the last page of v1's 8-page wizard. We only ever send the thumbnail there,
 * so a "last page" call would produce a zip containing nothing but the
 * thumbnail. Any non-last page skips the zip entirely. */
const THUMBNAIL_SYNC_PAGE = 1;

/** Where the wizard lands once each step's Continue succeeds: the 0-based step
 * index plus one, since the backend's `currentPage` is 1-based. useDraftResume
 * reads it straight back to decide which step to open on. */
const PAGE_AFTER_BASICS = 2;
const PAGE_AFTER_FILES = 3;
const PAGE_AFTER_TOOLS = 4;
const PAGE_AFTER_JOBDETAILS = 5;
const PAGE_AFTER_LICENSE = 6;

/** A file that still has its bytes — i.e. one the user dropped this session.
 * A restored file carries an attachmentId and no File, and is already on the
 * post, so it is never uploaded again. */
type PendingFile = V2File & { file: File };

function isPending(file: V2File): file is PendingFile {
  return !file.attachmentId && Boolean(file.file);
}

/** updatePost accepts any subset of the post fields, but IPostDataUploadRequest
 * declares them all required and types modelGroups as the persisted shape
 * (with _id/createdAt) rather than the request shape — so every patch payload
 * has to be cast at the request boundary. */
type PartialPostUpdate = Record<string, unknown>;

/**
 * Progressive persistence for the v2 wizard: each step's Continue button hands
 * its slice of the post to the backend, so nothing is lost if the user drops
 * out mid-flow. Every sync is idempotent — re-entering a step patches rather
 * than duplicating, and files already carrying an `attachmentId` are skipped.
 * Publishing is out of scope: the post stays a draft throughout.
 */
export default function useDraftSync() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    basics,
    files,
    videoLinks,
    thumbnailFileId,
    operations,
    extractedData,
    jobDetails,
    postId,
    mode,
    groupId,
    childIds,
  } = useSelector(selectV2);
  const multi = mode === 'multi';

  const [createDraftPost] = useCreateDraftPostMutation();
  const [createProjectGroup] = useCreateProjectGroupMutation();
  const [updateGroupProjects] = useUpdateProjectGroupProjectsMutation();
  const [updatePostData] = useUpdatePostDataMutation();
  const [updatePostAttachment] = useUpdatePostAttachmentMutation();
  const [getUploadUrls] = useGetPreSignedUploadUrlMutation();
  const [createTag] = useCreateTagMutation();
  const { data: categories } = useGetCategoriesQuery({
    type: ICategoryType.POST,
  });
  const { data: postTags } = useGetTagsQuery({ type: ITagType.POST });

  const resolveCategoryId = (): string => {
    const match = categories?.find(
      (c) =>
        c.name.trim().toLowerCase() === basics.category.trim().toLowerCase()
    );
    if (!match?._id) {
      throw new Error(
        `"${basics.category}" doesn't match a category on the server. Pick a different category and try again.`
      );
    }
    return match._id;
  };

  const findTagId = (name: string): string | undefined => {
    const wanted = name.trim().toLowerCase();
    return (postTags ?? []).find((t) => t.name.trim().toLowerCase() === wanted)
      ?._id;
  };

  /** An existing tag resolves to its id; an unknown one is created on the fly. */
  const resolveTagId = async (name: string): Promise<string> => {
    const existingId = findTagId(name);
    if (existingId) return existingId;
    const created = await createTag({ name, type: ITagType.POST }).unwrap();
    if (!created?._id) throw new Error(`Could not create tag "${name}"`);
    return created._id;
  };

  const resolveTagIds = async (): Promise<string[]> =>
    Promise.all(basics.tags.map(resolveTagId));

  /** Takes the already-resolved ids when the caller has them, so a create
   * followed by a patch doesn't resolve — and re-create — the same tags twice. */
  // fallow-ignore-next-line complexity
  const createDraft = async (
    resolvedCategoryId?: string,
    resolvedTagIds?: string[],
    // Passed in, not read off state: `ensureGroup` has only just dispatched it
    // and this closure still sees the old value.
    attachToGroupId?: string
  ): Promise<string> => {
    const categoryId = resolvedCategoryId ?? resolveCategoryId();
    const tagIds = resolvedTagIds ?? (await resolveTagIds());
    const draft = await createDraftPost(
      objectToFormData({
        title: basics.title,
        description: basics.description,
        category: categoryId,
        difficulty: basics.difficulty,
        tags: tagIds,
      })
    ).unwrap();
    dispatch(setPostId(draft._id));
    const attachTo = attachToGroupId ?? groupId;
    if (multi && attachTo) {
      dispatch(addChildId(draft._id));
      await updateGroupProjects({
        id: attachTo,
        data: { projects: [...childIds, draft._id] },
      }).unwrap();
      // The group's URL is what resumes the whole set; navigating to this one
      // child would strand the other workpieces.
      return draft._id;
    }
    // Move onto the draft's own URL so a refresh resumes it instead of creating
    // a second one. `replace` keeps it out of the history stack — Back should
    // leave the wizard, not return to the pre-draft upload URL.
    navigate(`/project/v2/edit/${draft._id}`, { replace: true });
    return draft._id;
  };

  /** The multi flow's container, created before its first workpiece so the
   * group's URL exists to resume from. Its name and category are placeholders —
   * the review screen collects the real ones. */
  // fallow-ignore-next-line complexity
  const ensureGroup = async (): Promise<string> => {
    if (groupId) return groupId;
    const created = await createProjectGroup({
      name: basics.title || 'Untitled multi-workpiece project',
      category: resolveCategoryId(),
      projects: [],
      inlineAuthored: true,
    }).unwrap();
    const id = created?.group?._id;
    if (!id) throw new Error('Could not start the multi-workpiece project');
    dispatch(setGroupId(id));
    navigate(`/project/v2/group/${id}`, { replace: true });
    return id;
  };

  /** Every later sync point needs a post to attach to. A draft normally
   * exists by then (Basics creates it), but the step rail lets the user skip
   * straight past Basics, so each one can create it on demand. */
  const ensureDraftId = async (): Promise<string> => postId ?? createDraft();

  const patchPost = async (postIdToPatch: string, data: PartialPostUpdate) =>
    updatePostData({
      postId: postIdToPatch,
      data: data as unknown as IPostDataUploadRequest,
    }).unwrap();

  const failWith = (title: string, err: unknown, fallback: string) => {
    toast.error(title, { description: extractErrorMessage(err, fallback) });
    return false;
  };

  /** Leaving Basics: create the draft, or patch it when the user came back to
   * edit what they'd already saved. The create endpoint accepts title,
   * description, category and tags and rejects every other key, so difficulty,
   * material, license and the resume page land in the patch that follows it. */
  const syncBasics = async (): Promise<boolean> => {
    try {
      const categoryId = resolveCategoryId();
      const tagIds = await resolveTagIds();
      const attachTo = multi ? await ensureGroup() : undefined;
      const draftId =
        postId ?? (await createDraft(categoryId, tagIds, attachTo));
      await patchPost(draftId, {
        title: basics.title,
        description: basics.description,
        category: categoryId,
        tags: tagIds,
        ...buildBasicsMetaPatch(basics),
        currentPage: PAGE_AFTER_BASICS,
      });
      return true;
    } catch (err) {
      return failWith(
        'Could not save your project',
        err,
        'Something went wrong while saving your project details.'
      );
    }
  };

  const uploadPendingFiles = async (draftId: string) => {
    const pending = files.filter(isPending);
    if (pending.length === 0) return;

    // Marked in flight up front, and cleared in the caller's finally — a file
    // must not be left spinning forever if the upload throws.
    dispatch(setUploadingFileIds(pending.map((f) => f.id)));

    const hashed = await Promise.all(
      pending.map(async (f) => ({
        v2Id: f.id,
        file: f.file,
        request: uploadRequestFor(
          f,
          draftId,
          (await hashFile(f.file)).toString()
        ),
      }))
    );

    const uploadUrls = await getUploadUrls({
      attachments: hashed.map((h) => h.request),
    }).unwrap();

    dispatch(setUploadTotal(uploadUrls.length));

    const attachmentIds: Record<string, string> = {};
    // By position: the response mirrors the request array. Matching on
    // fileHash resolved two byte-identical files to the same entry, so one was
    // uploaded twice and the other never at all.
    await Promise.all(
      uploadUrls.map(async (res, index) => {
        const match = hashed[index];
        if (!match) return;
        // A duplicate hash comes back with an attachment id but no signed URL
        // — the object is already in S3, so record the id and skip the PUT.
        if (res.attachmentId) attachmentIds[match.v2Id] = res.attachmentId;
        if (!res.signedUrl) return;
        await uploadFileToPresignedUrl(match.file, res.signedUrl);
        dispatch(incrementUploadProgress());
      })
    );
    dispatch(setFileAttachmentIds(attachmentIds));
  };

  /** Leaving Add files: push every file to S3 under its final name (G-code
   * names embed the controller the user just confirmed), set the chosen image
   * as the post thumbnail, then persist what the parser read out of the
   * G-code. The thumbnail is uploaded like any other image AND sent here — the
   * two land in different attachment categories (postMedia vs postThumbnail),
   * so this is not a double upload.
   *
   * A restored thumbnail has no bytes on the client and is already the post's
   * thumbnail, so it is left alone. */
  const syncFiles = async (): Promise<boolean> => {
    try {
      const draftId = await ensureDraftId();
      await uploadPendingFiles(draftId);

      const thumbnail = pickThumbnailFile(files, thumbnailFileId);
      const thumbnailUpload = thumbnail ? fileForUpload(thumbnail) : undefined;
      if (thumbnailUpload) {
        await updatePostAttachment({
          postId: draftId,
          currentPage: THUMBNAIL_SYNC_PAGE,
          data: objectToFormData({ thumbnail: thumbnailUpload }),
        }).unwrap();
      }

      // Always sent, even empty: the backend replaces the post's youtube
      // attachments with this list, so omitting it when the user removes their
      // last link would leave the old one on the post.
      await patchPost(draftId, {
        ...buildParserPatch(extractedData),
        youtubeAttachments: videoLinks,
        currentPage: PAGE_AFTER_FILES,
      });
      return true;
    } catch (err) {
      return failWith(
        'Upload failed',
        err,
        'Something went wrong while uploading your files. Please try again.'
      );
    } finally {
      dispatch(setUploadingFileIds([]));
    }
  };

  /** Leaving Tools: bit groups reference attachment ids, so this can only run
   * after syncFiles. */
  const syncModelGroups = async (): Promise<boolean> => {
    try {
      const draftId = await ensureDraftId();
      const modelGroups = buildModelGroups(
        operations,
        fileAttachmentIdMap(files)
      );
      await patchPost(draftId, {
        ...(modelGroups.length > 0 ? { modelGroups } : {}),
        currentPage: PAGE_AFTER_TOOLS,
      });
      return true;
    } catch (err) {
      return failWith(
        'Could not save your bits',
        err,
        'Something went wrong while saving your bit assignments.'
      );
    }
  };

  /** Leaving Job details: persist the editable job setup. Runs after Tools, so
   * the draft already exists. The meta patch rides along because the material
   * picker lives on this step, and it is the only thing that saves it here. */
  const syncJobDetails = async (): Promise<boolean> => {
    try {
      const draftId = await ensureDraftId();
      await patchPost(draftId, {
        ...buildBasicsMetaPatch(basics),
        ...buildJobDetailsPatch(jobDetails),
        currentPage: PAGE_AFTER_JOBDETAILS,
      });
      return true;
    } catch (err) {
      return failWith(
        'Could not save your job details',
        err,
        'Something went wrong while saving your job details.'
      );
    }
  };

  /** Leaving License. It rides in `buildBasicsMetaPatch` with the material and
   * difficulty, which are already saved — resending them is what keeps one
   * builder for the set rather than splitting it by which step collects what. */
  const syncLicense = async (): Promise<boolean> => {
    try {
      const draftId = await ensureDraftId();
      await patchPost(draftId, {
        ...buildBasicsMetaPatch(basics),
        currentPage: PAGE_AFTER_LICENSE,
      });
      return true;
    } catch (err) {
      return failWith(
        'Could not save your license',
        err,
        'Something went wrong while saving your license choice.'
      );
    }
  };

  return {
    ensureDraftId,
    patchPost,
    resolveTagIds,
    syncBasics,
    syncFiles,
    syncModelGroups,
    syncJobDetails,
    syncLicense,
  };
}
